import React from "react";
import { Link } from "react-router-dom";
import { Globe, Smartphone, TrendingUp, ShoppingBag, Code2, Palette, ArrowRight } from "lucide-react";

export default function ServicesSection() {
  const serviceList = [
    {
      icon: <Globe className="text-blue-600 dark:text-cyan-400" size={32} />,
      title: "🌐 Website Development",
      path: "/services/website-development",
      desc: "Crafting high-performance, custom websites using cutting-edge architectures like React, Next.js, PHP, WordPress, and Shopify.",
      tags: ["React 19", "Next.js", "WordPress", "Shopify", "PHP", "Tailwind CSS"]
    },
    {
      icon: <Smartphone className="text-indigo-600 dark:text-violet-400" size={32} />,
      title: "📱 App Development",
      path: "/services/app-development",
      desc: "Engineering native and cross-platform mobile applications optimized flawlessly for iOS and Android environments.",
      tags: ["iOS (Swift)", "Android (Kotlin)", "React Native", "Flutter", "Mobile UI"]
    },
    {
      icon: <TrendingUp className="text-blue-600 dark:text-blue-400" size={32} />,
      title: "🚀 Digital Marketing & SEO",
      path: "/services/digital-marketing-seo",
      desc: "Accelerating brand visibility and driving measurable business growth through smart, data-driven marketing strategies.",
      tags: ["SEO Optimization", "Social Media Marketing", "Performance Ads", "Brand Growth"]
    },
    {
      icon: <ShoppingBag className="text-emerald-600 dark:text-emerald-400" size={32} />,
      title: "🛒 E-Commerce Solutions",
      path: "/services/ecommerce-solutions",
      desc: "Turnkey online stores built on Shopify & WooCommerce with high conversion checkout flows and payment integration.",
      tags: ["Shopify Store", "WooCommerce", "Payment Gateways", "Conversion Rate"]
    },
    {
      icon: <Code2 className="text-rose-600 dark:text-rose-400" size={32} />,
      title: "💻 Custom Enterprise Software",
      path: "/services/custom-enterprise-software",
      desc: "Scalable backend systems, REST & GraphQL APIs, cloud microservices, and dedicated maintenance contracts.",
      tags: ["Node.js", "PostgreSQL", "Cloud Infrastructure", "API Integration"]
    },
    {
      icon: <Palette className="text-amber-600 dark:text-amber-400" size={32} />,
      title: "✨ UI/UX & Logo Design",
      path: "/services/ui-ux-logo-design",
      desc: "Modern digital interface design, interactive prototyping, and brand identity systems that captivate users.",
      tags: ["Figma Design", "UI/UX Design", "Responsive Layouts", "Branding"]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-body py-16 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-16">
        
        {/* HEADER */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-cyan-500/10 px-3.5 py-1.5 rounded-full border border-blue-200 dark:border-cyan-500/20 font-heading">
            Our Services & Solutions
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white">
            Engineering High-Performance <span className="text-gradient-cyan">Websites, Apps & Marketing</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base leading-relaxed font-medium">
            Hickter is a forward-thinking technology solutions company engineered to solve complex digital challenges. We help businesses scale through modern websites, apps, and smart digital marketing.
          </p>
        </div>

        {/* SERVICES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {serviceList.map((srv, idx) => (
            <div key={idx} className="glass-card p-8 rounded-2xl space-y-5 flex flex-col justify-between group">
              <div className="space-y-4">
                <div className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-900 w-fit border border-slate-200 dark:border-white/10 group-hover:scale-110 transition-transform">
                  {srv.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-cyan-300 transition-colors">
                  {srv.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed font-medium">
                  {srv.desc}
                </p>
              </div>

              <div className="space-y-4 pt-2">
                <div className="flex flex-wrap gap-1.5">
                  {srv.tags.map((tag, tIdx) => (
                    <span key={tIdx} className="text-[11px] text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-900 px-2.5 py-1 rounded-lg border border-slate-200 dark:border-white/10 font-medium">
                      {tag}
                    </span>
                  ))}
                </div>

                <Link to={srv.path} className="inline-flex items-center gap-2 text-xs font-bold text-blue-600 dark:text-cyan-400 hover:text-blue-700 dark:hover:text-cyan-300 font-heading pt-2 group-hover:translate-x-1 transition-transform">
                  <span>Explore Service</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* BOTTOM CTA */}
        <div className="glass-panel p-10 rounded-3xl text-center space-y-6 max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            Ready to Build Your Next Website or App?
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm max-w-xl mx-auto font-medium">
            Reach out to our engineering team for a technical consultation or project scope quote.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link to="/enquirynow" className="btn-primary-blue py-3 px-6 text-sm font-bold font-heading rounded-xl shadow-lg shadow-blue-500/20">
              Get Project Quote
            </Link>
            <Link to="/Meeting" className="btn-outline py-3 px-6 text-sm font-bold font-heading rounded-xl">
              Book Discovery Call
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
